import { skipToken } from '@reduxjs/toolkit/query';
import PermissionButton from 'components/buttons/PermissionButton';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { Delete as DeleteIcon, Edit as EditIcon } from 'components/icons';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { useModal } from 'components/modals/useModal';
import { ActiveChip } from 'components/record-header/RecordHeader';
import { DataTable } from 'components/tables/data-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { Button } from 'components/ui/button';
import { Tooltip } from 'components/ui/tooltip';
import { usePartyAssetPermissions } from 'pages/client-assets/usePartyAssetPermissions';
import { ReactNode, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useGetPartyAssetFundAccountsQuery } from 'store/api/partyAssets';
import { useUpdateClientAssetAccountsMutation } from 'store/api/tenants';
import { ClientAssetAccount, TenantConfigurationDto } from 'types/tenants';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';

type AccountRow = ClientAssetAccount & { index: number; inUse: boolean; holdsFunds: boolean };

function AccountNameModal({
    name,
    onSave,
    onClose,
}: {
    name?: string;
    onSave: (name: string) => void;
    onClose: () => void;
}) {
    const { control, handleSubmit } = useForm<{ name: string }>({ defaultValues: { name: name ?? '' } });
    return (
        <>
            <ModalHeader title={t('configuration.client-asset-accounts')} onCancel={onClose} />
            <div className="px-[23px] pt-[15px] pb-[45px]">
                <TextInput
                    control={control}
                    name="name"
                    label="configuration.client-asset-account-name"
                    required
                    validate={(value) => value.trim() !== '' || t('validation.required-field')}
                />
            </div>
            <ModalFooter onCancel={onClose} onSave={() => void handleSubmit((data) => onSave(data.name.trim()))()} />
        </>
    );
}

/** A disabled control says why on hover; the span takes the hover a disabled button does not. */
const WhyDisabled = ({ reason, children }: { reason?: string; children: ReactNode }) =>
    reason ? (
        <Tooltip content={reason}>
            <span>{children}</span>
        </Tooltip>
    ) : (
        children
    );

/**
 * The fund accounts client asset records are booked on. A name records use is locked, and an account whose records
 * still hold funds stays active. Without a client asset privilege the usage is not readable here, so the save's own
 * refusal says why instead.
 */
export function ClientAssetAccountsControl({ tenantConfiguration }: { tenantConfiguration: TenantConfigurationDto }) {
    const { openModal, closeModal } = useModal();
    const canEdit = useHasPermissions(['UPDATE.BACK_OFFICE_SETTINGS']);
    const { canUseForm } = usePartyAssetPermissions();
    const { data: usage } = useGetPartyAssetFundAccountsQuery(canUseForm ? undefined : skipToken);
    const [updateAccounts] = useUpdateClientAssetAccountsMutation();
    const accounts = tenantConfiguration.clientAssetAccounts;

    const rows = useMemo<AccountRow[]>(
        () =>
            accounts.map((account, index) => {
                const used = usage?.find(({ name }) => name === account.name);
                return { ...account, index, inUse: !!used?.inUse, holdsFunds: !!used?.holdsFunds };
            }),
        [accounts, usage]
    );

    // The API layer toasts the backend's reason for a refusal; a modal stays open to correct it.
    const save = useCallback(
        (next: ClientAssetAccount[], onSaved?: () => void) =>
            updateAccounts({ clientAssetAccounts: next })
                .unwrap()
                .then(() => onSaved?.())
                .catch(() => undefined),
        [updateAccounts]
    );

    const openNameModal = useCallback(
        (row?: AccountRow) =>
            openModal(
                <AccountNameModal
                    name={row?.name}
                    onClose={closeModal}
                    onSave={(name) =>
                        void save(
                            row
                                ? accounts.map((account, index) =>
                                      index === row.index ? { ...account, name } : account
                                  )
                                : [...accounts, { name, active: true }],
                            closeModal
                        )
                    }
                />,
                closeModal
            ),
        [accounts, save, openModal, closeModal]
    );

    const columns = useMemo<DataTableColumnDef<AccountRow>[]>(() => {
        const confirmDelete = (row: AccountRow) =>
            openModal(
                <ConfirmationModal
                    content={t('configuration.client-asset-account-delete-confirmation', { name: row.name })}
                    onCancel={closeModal}
                    onConfirm={() =>
                        void save(accounts.filter((_account, index) => index !== row.index)).finally(closeModal)
                    }
                />,
                closeModal
            );

        const setActive = (row: AccountRow, active: boolean) =>
            save(accounts.map((account, index) => (index === row.index ? { ...account, active } : account)));

        return [
            {
                header: t('configuration.client-asset-account-name'),
                id: 'name',
                accessorKey: 'name',
                enableSorting: false,
                meta: { widthWeight: 3, absorbSlack: true },
            },
            {
                header: t('configuration.client-asset-account-status'),
                id: 'active',
                enableSorting: false,
                meta: { widthWeight: 1 },
                cell: ({ row }) => <ActiveChip isActive={row.original.active} />,
            },
            {
                header: '',
                id: 'actions',
                enableSorting: false,
                meta: { fixedPx: 200, textAlign: 'right' },
                cell: ({ row: { original: row } }) => {
                    if (!canEdit) {
                        return null;
                    }
                    const lockedReason = row.inUse ? t('configuration.client-asset-account-in-use') : undefined;
                    const fundsReason =
                        row.active && row.holdsFunds ? t('configuration.client-asset-account-holds-funds') : undefined;
                    return (
                        <div className="flex items-center gap-1">
                            <WhyDisabled reason={fundsReason}>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    disabled={!!fundsReason}
                                    onClick={() => void setActive(row, !row.active)}
                                >
                                    {t(row.active ? 'configuration.deactivate' : 'configuration.reactivate')}
                                </Button>
                            </WhyDisabled>
                            <WhyDisabled reason={lockedReason}>
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    aria-label={t('edit')}
                                    disabled={row.inUse}
                                    onClick={() => openNameModal(row)}
                                >
                                    <EditIcon />
                                </Button>
                            </WhyDisabled>
                            <WhyDisabled reason={lockedReason}>
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    className="text-destructive"
                                    aria-label={t('delete')}
                                    disabled={row.inUse}
                                    onClick={() => confirmDelete(row)}
                                >
                                    <DeleteIcon />
                                </Button>
                            </WhyDisabled>
                        </div>
                    );
                },
            },
        ];
    }, [accounts, canEdit, closeModal, openModal, openNameModal, save]);

    return (
        <>
            <h3 className="text-2xl leading-8 font-semibold text-balance">
                <FormattedMessage id="configuration.client-asset-accounts" />
            </h3>
            <div className="flex flex-col gap-3">
                <DataTable<AccountRow>
                    columns={columns}
                    data={rows}
                    getRowId={(row) => String(row.index)}
                    columnConfigId="configuration-client-asset-accounts"
                />
                <div>
                    <PermissionButton
                        permissions={['UPDATE.BACK_OFFICE_SETTINGS']}
                        variant="outlined"
                        size="small"
                        className="h-8"
                        onClick={() => openNameModal()}
                    >
                        <FormattedMessage id="configuration.client-asset-account-add" />
                    </PermissionButton>
                </div>
            </div>
        </>
    );
}
